// @ts-nocheck

import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

import { KLCollectibleStatuses, KLCollectibleIds, type KLCollectibleId, type KLComfortId } from '../types/collectible';
import { type KLNotificationType, KLNotificationTypes } from '../types/notification';
import { type KLRound } from '../types/round';
import { KLViewTypes } from '../types/view';

import KLNotificationCollar from './common/KLNotificationCollar';
import KLNotificationCollectible from './common/KLNotificationCollectible';
import KLNotificationFall from './common/KLNotificationFall';
import KLNotificationPole from './common/KLNotificationPole';
import KLNotificationRoundComplete from './common/KLNotificationRoundComplete';
import KLNotificationTorch from './common/KLNotificationTorch';
import KLNotificationVictory from './common/KLNotificationVictory';
import KLNotificationVote from './common/KLNotificationVote';
import KLViewWrapper from './common/KLViewWrapper';

const backgroundAnimation = keyframes`
  0% {
    filter: brightness(1);
  }
  100% {
    filter: brightness(0.4);
  }
`;

const StyledKLViewWrapper = styled(KLViewWrapper)`
  padding: 0;

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;

    background: inherit;

    animation: ${backgroundAnimation} 1s ease 0.5s;
    animation-fill-mode: forwards;
  }
`;

const InnerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  padding: 1em;
`;

type KLNotificationProps = {
  className?: string;
  type: KLNotificationType;
  data?: {
    betslipReference: string;
    collectibleId?: KLCollectibleId;
    comfortId?: KLComfortId;
    remainingKnifes?: number;
    roundId?: KLRound;
    amount?: number;
    previousTime?: number;
    totalTime?: number;
    votes?: number;
    hasCollar?: boolean;
  };
  currentRound: KLRound;
};

const KLNotification = ({
  type,
  data: {
    betslipReference,
    collectibleId,
    comfortId,
    remainingKnifes,
    roundId,
    amount,
    previousTime,
    totalTime,
    votes,
    hasCollar,
  } = {},
  currentRound,
}: KLNotificationProps) => {
  const collectible = useMemo(() => {
    switch (type) {
      case KLNotificationTypes.COLLAR_WON:
      case KLNotificationTypes.COLLAR_LOST:
        return KLCollectibleIds.COLLAR;
      case KLNotificationTypes.MARK_WON:
      case KLNotificationTypes.MARK_LOST:
        return KLCollectibleIds.MARK;
      case KLNotificationTypes.TAG_WON:
      case KLNotificationTypes.TAG_LOST:
        return KLCollectibleIds.TAG;
      case KLNotificationTypes.KNIFE_WON:
      case KLNotificationTypes.KNIFE_LOST:
      case KLNotificationTypes.KNIFE_MISS:
        return KLCollectibleIds.KNIFE;
      default:
        return collectibleId ?? comfortId;
    }
  }, [type, collectibleId, comfortId]);

  const notification = useMemo(() => {
    const key = `notif-${betslipReference ?? ''}`;

    switch (type) {
      case KLNotificationTypes.BADGE_WON:
      case KLNotificationTypes.COMFORT_WON:
      case KLNotificationTypes.BADGE_LOST:
      case KLNotificationTypes.COMFORT_LOST:
      case KLNotificationTypes.COLLAR_WON:
      case KLNotificationTypes.COLLAR_LOST:
      case KLNotificationTypes.MARK_WON:
      case KLNotificationTypes.MARK_LOST:
      case KLNotificationTypes.TAG_WON:
      case KLNotificationTypes.TAG_LOST:
      case KLNotificationTypes.KNIFE_WON:
      case KLNotificationTypes.KNIFE_LOST:
      case KLNotificationTypes.KNIFE_MISS: {
        const hasWonCollectible = new Set([
          KLNotificationTypes.BADGE_WON,
          KLNotificationTypes.COMFORT_WON,
          KLNotificationTypes.COLLAR_WON,
          KLNotificationTypes.MARK_WON,
          KLNotificationTypes.TAG_WON,
          KLNotificationTypes.KNIFE_WON,
        ]).has(type);

        const hasMissCollectible = type === KLNotificationTypes.KNIFE_MISS;

        const isOrientation = new Set([KLCollectibleIds.MARK, KLCollectibleIds.TAG, KLCollectibleIds.KNIFE]).has(
          collectible,
        );

        return collectible !== undefined
          ? {
              background: isOrientation ? KLViewTypes.ORIENTATION : KLViewTypes.BADGE_DETAIL,
              content: (
                <KLNotificationCollectible
                  key={key}
                  collectibleId={collectible}
                  collectibleStatus={
                    hasWonCollectible
                      ? KLCollectibleStatuses.WON
                      : hasMissCollectible
                        ? KLCollectibleStatuses.MISS
                        : KLCollectibleStatuses.LOST
                  }
                  remainingKnifes={remainingKnifes}
                  amount={amount}
                  betslipReference={betslipReference}
                />
              ),
            }
          : null;
      }
      case KLNotificationTypes.PRICE_WON:
        return amount !== undefined
          ? {
              background: roundId ?? currentRound,
              content: <KLNotificationVictory key={key} amount={amount} round={roundId ?? currentRound} />,
            }
          : null;
      case KLNotificationTypes.VOTE_PENALTY:
        return votes !== undefined
          ? {
              background: KLViewTypes.COUNCIL,
              content: <KLNotificationVote key={key} votes={votes} hasCollar={hasCollar} />,
            }
          : null;
      case KLNotificationTypes.COLLAR_USED:
        return { background: KLViewTypes.COUNCIL, content: <KLNotificationCollar key={key} /> };
      case KLNotificationTypes.TORCH:
        return { background: KLViewTypes.COUNCIL, content: <KLNotificationTorch key={key} roundId={currentRound} /> };
      case KLNotificationTypes.ROUND_COMPLETE:
        return roundId !== undefined
          ? { background: undefined, content: <KLNotificationRoundComplete key={key} roundId={roundId} /> }
          : null;
      case KLNotificationTypes.POLE_BET_WON:
      case KLNotificationTypes.POLE_BET_LOST:
        return totalTime !== undefined
          ? {
              background: KLViewTypes.POLE,
              content: (
                <KLNotificationPole
                  key={key}
                  previousTime={previousTime}
                  totalTime={totalTime}
                  hasWon={type === KLNotificationTypes.POLE_BET_WON}
                  betslipReference={betslipReference}
                />
              ),
            }
          : null;
      case KLNotificationTypes.FALL:
        return totalTime !== undefined
          ? { background: undefined, content: <KLNotificationFall totalTime={totalTime} /> }
          : null;
      default:
        return null;
    }
  }, [
    type,
    votes,
    hasCollar,
    betslipReference,
    amount,
    roundId,
    collectible,
    remainingKnifes,
    currentRound,
    previousTime,
    totalTime,
  ]);

  return notification ? (
    <StyledKLViewWrapper viewType={notification.background}>
      <InnerWrapper>{notification.content}</InnerWrapper>
    </StyledKLViewWrapper>
  ) : null;
};

export default KLNotification;
